// hooks/useGoogleTagManager.js
"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { readJsonCookie, getCookie } from "@/lib/cookies";

// Prefer configuring these in env
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID || "GTM-WFGMH2DX";
// If you ALSO want to load GA directly (outside GTM), set GA_ID
const GA_ID = process.env.NEXT_PUBLIC_GA_ID || "G-91M1BQLTVL";
// If you ALSO want AdSense directly
const ADSENSE_ID =
    process.env.NEXT_PUBLIC_ADSENSE_ID || "ca-pub-9095963128435010";

const DEFAULT_CONSENT = {
    analytics: false,
    ads: false,
    personalization: false,
    preferences: false,
    accepted: false,
};

function ensureDataLayer() {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
    }
}

function setConsentDefault() {
    // Consent Mode v2 default (blocked) BEFORE any tags load
    window.gtag("consent", "default", {
        ad_storage: "denied",
        analytics_storage: "denied",
        personalization_storage: "denied",
        functionality_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        wait_for_update: 500, // ms
    });
}

function updateConsent(consent) {
    window.gtag("consent", "update", {
        ad_storage: consent.ads ? "granted" : "denied",
        analytics_storage: consent.analytics ? "granted" : "denied",
        personalization_storage: consent.personalization ? "granted" : "denied",
        functionality_storage: consent.preferences ? "granted" : "denied",
        ad_user_data:
            consent.ads || consent.personalization ? "granted" : "denied",
        ad_personalization: consent.personalization ? "granted" : "denied",
    });
}

function readCspNonce() {
    if (typeof document === "undefined") return "";
    const metaNonce = document
        .querySelector('meta[name="x-nonce"]')
        ?.getAttribute("content");
    return metaNonce || getCookie("csp_nonce") || "";
}

/** idempotent script injector */
function appendScriptOnce({ id, src, async = true, crossOrigin, nonce }) {
    if (typeof document === "undefined") return;
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.async = async;
    s.src = src;
    if (nonce) s.nonce = nonce;
    if (crossOrigin) s.crossOrigin = crossOrigin;
    document.head.appendChild(s);
}

/** Only call after consent default is set */
function loadVendors({ allowGTM, allowGA, allowAds }) {
    const nonce = readCspNonce();

    if (allowGTM) {
        appendScriptOnce({
            id: "gtm-script",
            src: `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`,
            nonce,
        });
    }

    // If you’re using GTM to load GA/Ads, you can skip these two.
    if (allowGA && !allowGTM && GA_ID) {
        appendScriptOnce({
            id: "ga-script",
            src: `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
            nonce,
        });
        // Minimal GA config after library arrives (safe even if it’s a bit late)
        const poll = setInterval(() => {
            if (typeof window.gtag === "function") {
                window.gtag("js", new Date());
                window.gtag("config", GA_ID, { anonymize_ip: true });
                clearInterval(poll);
            }
        }, 200);
        setTimeout(() => clearInterval(poll), 5000);
    }

    if (allowAds && !allowGTM && ADSENSE_ID) {
        appendScriptOnce({
            id: "adsense-script",
            src: `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_ID}`,
            crossOrigin: "anonymous",
            nonce,
        });
    }
}

export function useGoogleTagManager() {
    const [consent, setConsent] = useState(DEFAULT_CONSENT);
    const consentRef = useRef(DEFAULT_CONSENT);
    const isProduction = process.env.NODE_ENV === "production";

    const loadConsentFromCookie = useCallback(() => {
        // Read your JSON cookie written by CookieConsentProvider
        const cookieObj = readJsonCookie("cookie_consent");
        const normalized = { ...DEFAULT_CONSENT, ...(cookieObj || {}) };
        if (JSON.stringify(normalized) !== JSON.stringify(consentRef.current)) {
            consentRef.current = normalized;
            setConsent(normalized);
        }
    }, []);

    useEffect(() => {
        if (!isProduction || typeof window === "undefined") return;
        const onUpdate = (e) => {
            const next = { ...DEFAULT_CONSENT, ...(e.detail || {}) };
            if (JSON.stringify(next) !== JSON.stringify(consentRef.current)) {
                consentRef.current = next;
                setConsent(next);
            }
        };
        window.addEventListener("cookieconsent:updated", onUpdate);
        return () =>
            window.removeEventListener("cookieconsent:updated", onUpdate);
    }, [isProduction]);

    // On mount: 1) create dataLayer/gtag, 2) set default deny BEFORE loading vendors
    useEffect(() => {
        if (!isProduction || typeof window === "undefined") return;
        ensureDataLayer();
        window.gtag("js", new Date()); // initialize timeline
        setConsentDefault();
        loadConsentFromCookie();
    }, [isProduction, loadConsentFromCookie]);

    // If another tab updates consent cookie (optional: depends on your UI)
    // We can poll the cookie every few seconds, since storage event doesn’t fire for cookies.
    useEffect(() => {
        if (!isProduction) return;
        const t = setInterval(loadConsentFromCookie, 3000);
        return () => clearInterval(t);
    }, [isProduction, loadConsentFromCookie]);

    // When consent changes: update Consent Mode and conditionally load scripts
    useEffect(() => {
        if (!isProduction || typeof window === "undefined") return;

        updateConsent(consent);

        // Decide what to load: usually GTM only (it can manage GA/Ads inside)
        const allowGTM =
            consent.analytics || consent.ads || consent.personalization;
        const allowGA = consent.analytics; // only if you want to load GA directly
        const allowAds = consent.ads; // only if you want to load AdSense directly

        loadVendors({ allowGTM, allowGA, allowAds });
    }, [consent, isProduction]);

    // (Optional) Expose small helper for inline components if needed later
    return {
        consent,
        country: (getCookie("user_country") || "ZZ").toUpperCase(),
    };
}
